'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Modal } from '@common/components';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useOverlayHistory } from '@/hooks/useOverlayHistory';
import { useAdGuards } from '@/modules/adpopcorn/_hooks/useAdGuards';
import { useAuthStore } from '@/stores/AuthStore';
import { useGlobalStore } from '@/stores/GlobalStore';
import { closeResponsiveDialog, openResponsiveDialog } from '@/stores/ResponsiveDialogStore';
import { BenefitConsentSheet } from '../_components/BenefitConsentSheet';
import { useAcceptMarketingConsent } from './useAcceptMarketingConsent';

/**
 * 리워드 행위별 검증 게이트. 소비처가 steps 배열 순서로 조립한다.
 * 권장 순서 = 앱 → 로그인 → 19+ → 동의: 로그인 후 나이를 확보해 19세 미만을
 * 동의(consent) 저장 전에 차단한다(미성년 마케팅 동의 persist 방지).
 * - app/login/adult: 미충족 시 유도(스토어/리다이렉트/모달)하고 원행위를 포기(terminate)
 * - consent: S8 동의 시트 → 동의 시 원행위를 이어간다(resume, 남은 스텝을 클로저로 재개)
 * - adult: 나이 미확인(undefined) 시에도 안전하게 차단(19+ 전용 기능)
 */
export type BenefitGateStep = 'app' | 'login' | 'consent' | 'adult';
export const useBenefitGate = () => {
  const $ = _c(18);
  const router = useCustomRouter();
  const pathname = usePathname();
  const isLogin = useAuthStore(_temp);
  const age = useAuthStore(_temp2);
  const isConsented = useAuthStore(_temp3);
  const {
    isWelloApp
  } = useAdGuards();
  const setIsShowAppInstallConfirmModal = useGlobalStore(_temp4);
  const acceptConsent = useAcceptMarketingConsent();
  const [isAdultModalOpen, setIsAdultModalOpen] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => setIsAdultModalOpen(false);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useOverlayHistory(isAdultModalOpen, t0);
  let t1;
  if ($[1] !== acceptConsent || $[2] !== age || $[3] !== isConsented || $[4] !== isLogin || $[5] !== isWelloApp || $[6] !== pathname || $[7] !== router || $[8] !== setIsShowAppInstallConfirmModal) {
    const triggerGateFrom = (startIndex, steps, onPass) => {
      for (let index = startIndex; index < steps.length; index = index + 1, index) {
        const step = steps[index];
        if (step === "app" && !isWelloApp) {
          setIsShowAppInstallConfirmModal(true);
          return;
        }
        if (step === "login" && !isLogin) {
          router.push(ROUTES.LOGIN.withSearchParams({
            searchParams: {
              redirect: pathname ?? ""
            }
          }));
          return;
        }
        if (step === "consent" && !isConsented) {
          const resumeSteps = steps.slice(index + 1);
          openResponsiveDialog({
            component: BenefitConsentSheet,
            ariaLabel: "\uD61C\uD0DD \uC11C\uBE44\uC2A4 \uB3D9\uC758",
            drawerType: "bottom",
            props: {
              onAgree: () => acceptConsent.mutateAsync(undefined).then(() => {
                closeResponsiveDialog();
                triggerGateFrom(0, resumeSteps, onPass);
              })
            }
          });
          return;
        }
        if (step === "adult" && (age === undefined || age < 19)) {
          setIsAdultModalOpen(true);
          return;
        }
      }
      onPass();
    };
    t1 = (steps_0, onPass_0) => {
      triggerGateFrom(0, steps_0, onPass_0);
    };
    $[1] = acceptConsent;
    $[2] = age;
    $[3] = isConsented;
    $[4] = isLogin;
    $[5] = isWelloApp;
    $[6] = pathname;
    $[7] = router;
    $[8] = setIsShowAppInstallConfirmModal;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const triggerGate = t1;
  let t2;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setIsAdultModalOpen(false);
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex flex-col gap-12"><h2 className="font-700 text-Gray-900 typo-body1">만 19세 이상만 참여할 수 있어요</h2><p className="font-400 text-Gray-700 typo-body3">행운권·행운의 로또는 만 19세 이상만 이용할 수 있어요.</p></div>;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex flex-col gap-24 text-center">{t3}<Button onClick={() => setIsAdultModalOpen(false)} className="w-full">확인</Button></div>;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== isAdultModalOpen) {
    t5 = <Modal opened={isAdultModalOpen} onClose={t2}>{t4}</Modal>;
    $[13] = isAdultModalOpen;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const gateModals = t5;
  let t6;
  if ($[15] !== gateModals || $[16] !== triggerGate) {
    t6 = {
      triggerGate,
      gateModals
    };
    $[15] = gateModals;
    $[16] = triggerGate;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
function _temp(state) {
  return state.isLogin;
}
function _temp2(state_0) {
  return state_0.myInfo?.age;
}
function _temp3(state_1) {
  return state_1.myInfo?.notificationOptions.marketingMessageReceiveYN ?? false;
}
function _temp4(state_2) {
  return state_2.setIsShowAppInstallConfirmModal;
}